import type { NormalizedFieldSet, PriceField } from './types';

export type MinMaxOccupancy = {
	curAdults: number;
	curChildren: number;
	minAdults: number;
	maxAdults?: number;
	adultsField?: PriceField;
	minChildren: number;
	maxChildren?: number;
	childrenField?: PriceField;
	minTotal?: number;
	minMessage: string;
	maxTotal?: number;
	maxMessage: string;
};

/**
 * Ported 1:1 from the legacy `getMinMaxOcc.js`. Finds the fieldSet carrying `occupancyValidation`,
 * derives the adults/children guest-count fields, and computes the min/max bounds (including the
 * group total) plus translated guidance messages. Note this mutates `adultsField.min` /
 * `childrenField.min` in place when the sum of the individual mins is below the group minimum -
 * that mutation is load-bearing for `ProductItem`'s live re-clamping and is preserved here.
 */
export default function getMinMaxOcc({
	fieldSets,
	t,
}: {
	fieldSets: NormalizedFieldSet[];
	t: (text: string, params?: (string | number)[]) => string;
}): MinMaxOccupancy {
	const occupancyField = fieldSets.find(
		fieldSet => !fieldSet.hidden && fieldSet.label && fieldSet.occupancyValidation
	);

	const adultsField = occupancyField?.fields.find(field => field.name.includes('Number_of_Adults'));
	const childrenField = occupancyField?.fields.find(field =>
		field.name.includes('Number_of_Children')
	);

	let minAdults = adultsField?.min || 0;
	let minChildren = childrenField?.min || 0;

	const minTotal = occupancyField?.occupancyValidation?.min;

	let minMessage = '';
	const maxAdults = adultsField?.max;
	const maxChildren = childrenField?.max;
	const maxTotal = occupancyField?.occupancyValidation?.max;

	let maxMessage = '';
	let curAdults = minAdults;
	let curChildren = minChildren;

	// If there is a min total, make sure we start with at least that many guests
	const guestMin = minAdults + minChildren;
	if (minTotal && guestMin < minTotal) {
		if (adultsField) {
			curAdults += minTotal - guestMin;
			adultsField.min = curAdults;
			// If there is no children field, this must be the minimum adults value
			if (!childrenField) {
				minAdults = curAdults;
			}
		} else if (childrenField) {
			curChildren += minTotal - guestMin;
			childrenField.min = curChildren;
			// There is no adults field, so this must be the minimum children value
			minChildren = curChildren;
		}
	}

	if (minAdults || minChildren) {
		if (minAdults && !minChildren) {
			minMessage =
				minAdults === maxAdults
					? t(`This product requires and only accomodates {0} adults.`, [minAdults])
					: t(`Minimum {0} adult is required.`, [minAdults]);
		} else if (minChildren && !minAdults) {
			minMessage =
				minChildren === maxChildren
					? t(`This product requires and only accomodates {0} children.`, [minChildren])
					: t(`Minimum {0} children is required.`, [minChildren]);
		} else if (minAdults && minChildren) {
			minMessage = t(`Minimum {0} adult, {1} children are required.`, [minAdults, minChildren]);
		}
	}

	if (maxAdults && maxChildren) {
		maxMessage = t(`This product can accomodate a maximum of {0} adults, {1} children.`, [
			maxAdults,
			maxChildren,
		]);
	} else if (maxAdults) {
		maxMessage = t(`This product can accomodate a maximum of {0} adults.`, [maxAdults]);
	} else if (maxChildren) {
		maxMessage = t(`This product can accomodate a maximum of {0} children.`, [maxChildren]);
	}

	return {
		curAdults,
		curChildren,
		minAdults,
		maxAdults,
		adultsField,
		minChildren,
		maxChildren,
		childrenField,
		minTotal,
		minMessage,
		maxTotal,
		maxMessage,
	};
}
